import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { api } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import BotonExportar from '../ui/BotonExportar';
import { SelectFiltro } from '../ui/Filtros';
import { comoVaLaExactitud, colorDeExactitud } from '../ui/exactitud';
import { Cargando } from '../ui/EstadoDePantalla';

const VENTANAS = [
  { valor: '4', etiqueta: 'Últimas 4 semanas' },
  { valor: '12', etiqueta: 'Últimas 12 semanas' },
  { valor: '26', etiqueta: 'Últimos 6 meses' },
  { valor: '52', etiqueta: 'Último año' },
];

const COLS = [
  { etiqueta: 'Ubicación', valor: (c) => c.ubicacion },
  { etiqueta: 'Nombre', valor: (c) => c.nombre || '' },
  { etiqueta: 'Renglones contados', valor: (c) => c.renglones },
  { etiqueta: 'Exactitud %', valor: (c) => c.exactitud },
  { etiqueta: 'Tendencia (puntos)', valor: (c) => (c.tendencia == null ? '' : c.tendencia) },
];

const TINTA = {
  verde: 'text-green-700', ambar: 'text-amber-700',
  rojo: 'text-red-700', gris: 'text-gray-400',
};

// LA TENDENCIA DE EXACTITUD POR UBICACIÓN · TC-32 (§A4).
//
// Los conteos y sus diferencias existían desde la migración 014. Lo que
// no había era LA SERIE EN EL TIEMPO — que es lo único que contesta la
// pregunta que de verdad se hace un dueño: «¿mi inventario está mejorando
// o empeorando?». Un porcentaje suelto no la contesta.
export default function Exactitud() {
  const [semanas, setSemanas] = useState('12');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get(`/conteos/reportes/exactitud?semanas=${semanas}`)
      .then((d) => vigente && setDatos(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [semanas]);

  if (error) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>;
  }
  if (!datos) return <Cargando filas={4} />;

  // «CARGANDO» SIN TOCAR EL ESTADO EN EL EFECTO. La respuesta dice de
  // cuántas semanas es, así que si no coincide con lo que se pidió es
  // que la nueva todavía viene en camino. Se deduce en vez de guardarse:
  // un `setDatos(null)` dentro del efecto encadena renders, y el lint
  // del proyecto lo dice con razón.
  //
  // Y LA TABLA VIEJA SE QUEDA MIENTRAS TANTO, atenuada: vaciarla haría
  // parpadear la pantalla en cada cambio de periodo.
  const cargando = String(datos.semanas) !== String(semanas);
  const filas = datos.por_ubicacion || [];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Exactitud de inventario</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Cuántas veces lo que dice TEGO coincide con lo que hay en el estante, repisa por repisa
            y semana a semana.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {cargando && <span className="text-xs text-gray-500">Actualizando…</span>}
          <SelectFiltro valor={semanas} onChange={setSemanas} opciones={VENTANAS} />
        </div>
      </div>

      <NotaExplicativa titulo="Cómo se mide, para que el número signifique algo">
        Se cuentan <strong>renglones que coinciden</strong>, no piezas: un renglón con una pieza de más
        está mal igual que uno con mil. Lo que se quiere saber es cuántas veces el papel dice la verdad.
        {' '}Cada renglón entra en <strong>la semana en que se contó</strong>, no en la que se cerró la
        hoja. Y una repisa que solo se ha contado una vez <strong>no tiene tendencia</strong>: hace falta
        más de una semana para decir si va mejor o peor, y aquí no se inventa una flecha.
      </NotaExplicativa>

      {datos.exactitud_global !== null && datos.exactitud_global !== undefined && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase">Toda la empresa</p>
          <p className={`text-3xl font-bold ${TINTA[colorDeExactitud(datos.exactitud_global)]}`}>
            {datos.exactitud_global}%
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Sobre todos los renglones contados en el periodo, no el promedio de las repisas: una
            repisa con tres renglones perfectos no vale lo mismo que una con trescientos.
          </p>
        </div>
      )}

      <div className={cargando ? 'opacity-50 transition-opacity' : ''}>
      {filas.length === 0 ? (
        <EstadoVacio
          icono={Target}
          titulo="Todavía no has contado nada en este periodo"
          descripcion={'La exactitud sale de los conteos: hasta que alguien vaya al estante y cuente, '
            + 'no hay con qué compararla. Abre una hoja de conteo sobre una repisa y vuelve aquí.'}
        />
      ) : (
        <>
          <div className="flex justify-end">
            <BotonExportar nombre={`exactitud-${semanas}-semanas`} columnas={COLS} filas={filas} />
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
                  <th className="px-4 py-3">Repisa</th>
                  <th className="px-4 py-3 text-right">Renglones</th>
                  <th className="px-4 py-3 text-right">Exactitud</th>
                  <th className="px-4 py-3">Cómo va</th>
                  <th className="px-4 py-3">Semana a semana</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((c) => {
                  const va = comoVaLaExactitud(c.tendencia);
                  return (
                    <tr key={c.ubicacion} className="border-b border-gray-100">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-900">{c.ubicacion}</div>
                        <div className="text-xs text-gray-500">{c.nombre}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{c.renglones}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${TINTA[colorDeExactitud(c.exactitud)]}`}>
                        {c.exactitud}%
                      </td>
                      <td className={`px-4 py-2.5 text-xs ${TINTA[va.color]}`}>{va.frase}</td>
                      {/* LA SERIE, SIN BIBLIOTECA DE GRÁFICAS. Doce barras
                          en una celda dicen la forma de la curva, que es
                          para lo que se mira una tendencia; el número
                          exacto ya está en la columna de al lado. */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-end gap-0.5 h-8" title={
                          c.semanas.map((s) => `${s.semana}: ${s.exactitud}%`).join(' · ')
                        }>
                          {c.semanas.map((s) => (
                            <div
                              key={s.semana}
                              style={{ height: `${Math.max(4, s.exactitud)}%` }}
                              className={`w-2 rounded-sm ${
                                colorDeExactitud(s.exactitud) === 'verde' ? 'bg-green-600'
                                  : colorDeExactitud(s.exactitud) === 'ambar' ? 'bg-amber-500'
                                    : 'bg-red-600'
                              }`}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            Las repisas peores salen primero: es donde hay que mirar. Pasa el ratón por las barras
            para ver la semana y su porcentaje.
          </p>
        </>
      )}
      </div>
    </div>
  );
}
